import { CheckCircle as CheckCircleIcon } from "@mui/icons-material";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import {
  ButtonBase,
  Checkbox,
  IconButton,
  Paper,
  Typography,
  Box,
  Theme,
} from "@mui/material";
import React from "react";
import { cssValue } from "../../../common/MuiThemeProvider.js";
import { AttachmentSnapshot } from "../../../clients/graphqlTypes.js";
import defaultImageUrl from "../../assets/images/no-image.png";

interface Props {
  isPreselected: boolean;
  isGreyedOut: boolean;
  label: string;
  image?: Pick<AttachmentSnapshot, "url" | "title"> | null;
  multiple?: boolean;
  onClick(): void;
  withImage?: boolean;
  disabled?: boolean;
  /** Opens the option's details; rendered as its own control beside the tile. */
  onDetailsClick?: () => void;
  detailsLabel?: string;
}

export const PropertyOptionItem = ({
  isPreselected,
  isGreyedOut,
  onClick,
  label,
  image,
  multiple = false,
  withImage = false,
  disabled = false,
  onDetailsClick,
  detailsLabel,
}: Props) => {
  const tile = (
    <Paper
      disableRipple={true}
      component={ButtonBase}
      onClick={onClick}
      aria-pressed={isPreselected}
      sx={theme => ({
        transition: "background-color 0.2s ease-out",
        borderRadius: "4px",
        width: "100%",
        minHeight: "32px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        ...getButtonStyle({ theme, isPreselected, isGreyedOut }),
      })}
      disabled={disabled}
    >
      {withImage && (
        <Box
          sx={{
            flexShrink: "0",
            flexGrow: "0",
            width: "100%",
            overflow: "hidden",
            display: "flex",
            position: "relative",
            aspectRatio: "4 / 3",
            borderRadius: "4px 4px 0 0",
          }}
        >
          <img
            draggable={false}
            src={image?.url ?? defaultImageUrl}
            // Decorative: the label names the option, so the picture must not
            // prefix the tile's accessible name.
            alt=""
            style={{
              height: "100%",
              width: "100%",
              objectFit: "cover",
              borderRadius: "inherit",
            }}
          />
          {multiple ? (
            <Checkbox
              size="medium"
              checked={isPreselected}
              color="success"
              sx={theme => ({
                position: "absolute",
                top: "6px",
                left: "6px",
                padding: "2px",
                color: theme.palette.common.white,
                backgroundColor: "rgba(0,0,0,0.2)",
                transition:
                  "color 0.2s ease-out, background-color 0.2s ease-out",
                "&.Mui-checked": {
                  backgroundColor: "rgba(255,255,255,1)",
                },
              })}
              onClick={e => {
                e.stopPropagation();
                onClick();
              }}
            />
          ) : (
            <Checkbox
              size="medium"
              checked={isPreselected}
              color="success"
              icon={<CheckCircleIcon />}
              checkedIcon={<CheckCircleIcon />}
              sx={theme => ({
                ...(!isPreselected ? { display: "none" } : undefined),
                position: "absolute",
                top: "8px",
                left: "8px",
                padding: 0,
                "&.Mui-checked": {
                  backgroundColor: theme.palette.common.white,
                  "&.hover": {
                    backgroundColor: theme.palette.common.white,
                  },
                },
              })}
              onClick={e => {
                e.stopPropagation();
                onClick();
              }}
            />
          )}
        </Box>
      )}
      <Box
        sx={{
          p: 1,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: "bold",
          }}
        >
          {label}
        </Typography>
      </Box>
    </Paper>
  );

  if (!onDetailsClick) return tile;

  // A button inside the tile's ButtonBase would be a button within a button,
  // so the details control is laid over the tile as a sibling instead.
  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      {tile}
      <IconButton
        size="small"
        aria-label={`${detailsLabel}: ${label}`}
        disabled={disabled}
        onClick={event => {
          event.stopPropagation();
          onDetailsClick();
        }}
        sx={theme => ({
          position: "absolute",
          top: "6px",
          right: "6px",
          backgroundColor: "rgba(255,255,255,0.8)",
          "&:hover": { backgroundColor: theme.palette.common.white },
        })}
      >
        <InfoIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

const getButtonStyle = ({
  theme,
  isPreselected,
  isGreyedOut,
}: {
  theme: Theme;
  isPreselected: boolean;
  isGreyedOut: boolean;
}) => ({
  backgroundColor: isGreyedOut
    ? theme.palette.grey[100]
    : theme.palette.common.white,
  ...(!isPreselected
    ? {
        "&:hover:not(:has(button:hover)):not(:has(a:hover))": {
          backgroundColor: isGreyedOut
            ? theme.palette.grey[200]
            : theme.palette.grey[100],
        },
      }
    : undefined),
  ...(isPreselected
    ? {
        boxShadow: cssValue.preselectedOutlineShadow,
        borderColor: "transparent",
      }
    : undefined),
});
